/**
 * Axios compatibility bridge for older files.
 *
 * Newer code should use ../api/apiClient directly.
 * Older screens (including parts of Audition Rooms) still import raw axios
 * from 'axios', so this file keeps those requests on the same runtime backend
 * origin and auth lifecycle as apiClient without forcing a risky all-at-once
 * page rewrite.
 */

import axios from 'axios';
import apiClient from '../api/apiClient';

const BACKEND_URL = window.__BACKEND_URL__;
const BUILD_BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const getStoredToken = () => {
  try {
    return (
      localStorage.getItem('access_token') ||
      localStorage.getItem('token') ||
      localStorage.getItem('authToken') ||
      localStorage.getItem('ccuk_token')
    );
  } catch {
    return null;
  }
};

const normaliseLegacyBackendUrl = (config) => {
  if (!config || !BACKEND_URL) return config;

  const url = config.url || '';
  const baseURL = config.baseURL || '';

  // Relative protected routes always belong on the runtime-resolved backend.
  if (url.startsWith('/api/')) {
    config.baseURL = BACKEND_URL;
    return config;
  }

  // A number of older screens interpolate REACT_APP_BACKEND_URL into an
  // absolute URL. The bundle may have been built in preview and later served
  // on production (or vice versa), so replace ONLY that known build-time
  // backend origin with the runtime resolver's origin while preserving the
  // path/query. Never rewrite arbitrary third-party absolute URLs.
  if (
    BUILD_BACKEND_URL &&
    BUILD_BACKEND_URL !== BACKEND_URL &&
    url.startsWith(BUILD_BACKEND_URL) &&
    url.slice(BUILD_BACKEND_URL.length).startsWith('/api/')
  ) {
    config.url = `${BACKEND_URL}${url.slice(BUILD_BACKEND_URL.length)}`;
    delete config.baseURL;
    return config;
  }

  if (
    BUILD_BACKEND_URL &&
    BUILD_BACKEND_URL !== BACKEND_URL &&
    baseURL === BUILD_BACKEND_URL &&
    url.startsWith('/api/')
  ) {
    config.baseURL = BACKEND_URL;
  }

  return config;
};

const isBackendApiRequest = (config) => {
  const requestUrl = `${config?.baseURL || ''}${config?.url || ''}`;
  return (
    requestUrl.startsWith('/api/') ||
    requestUrl.includes('/api/') ||
    (BACKEND_URL && requestUrl.startsWith(BACKEND_URL))
  );
};

if (typeof window !== 'undefined' && !window.__CCUK_RAW_AXIOS_AUTH_BRIDGE__) {
  window.__CCUK_RAW_AXIOS_AUTH_BRIDGE__ = true;

  axios.interceptors.request.use(
    (config) => {
      normaliseLegacyBackendUrl(config);
      const token = getStoredToken();

      if (token && isBackendApiRequest(config)) {
        config.headers = config.headers || {};

        if (!config.headers.Authorization) {
          config.headers.Authorization = `Bearer ${token}`;
        }
      }

      return config;
    },
    (error) => Promise.reject(error)
  );

  // Raw axios historically attached the access token but stopped at the
  // first 401. That meant a long-lived Audition Room page could work on
  // entry and then fail after the short access token expired. Delegate ONE
  // protected 401 to apiClient: its normal response interceptor performs the
  // refresh-token exchange, updates tokenManager/localStorage, and retries the
  // original request with the new Bearer token.
  axios.interceptors.response.use(
    (response) => response,
    async (error) => {
      const config = error?.config;
      const requestUrl = `${config?.baseURL || ''}${config?.url || ''}`;
      const isAuthBootstrap = (
        requestUrl.includes('/api/auth/login') ||
        requestUrl.includes('/api/auth/register') ||
        requestUrl.includes('/api/auth/refresh')
      );

      if (
        error?.response?.status === 401 &&
        config &&
        isBackendApiRequest(config) &&
        !isAuthBootstrap &&
        !config._ccukRawAuthForwarded
      ) {
        const retryConfig = {
          ...config,
          _ccukRawAuthForwarded: true,
          headers: { ...(config.headers || {}) },
        };
        normaliseLegacyBackendUrl(retryConfig);
        // Do not pin the stale Bearer token. apiClient will attach the current
        // token, and if that token is also expired its own 401 interceptor will
        // perform exactly one refresh before retrying.
        delete retryConfig.headers.Authorization;
        delete retryConfig.headers.authorization;
        return apiClient.request(retryConfig);
      }

      return Promise.reject(error);
    }
  );
}

export default apiClient;
